import type { TableReservation, VendorShow } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { TableCheckoutSheet } from "../../../../components/TableCheckoutSheet";
import { showDates, venueLine } from "../../../../core/primitives/hosted-event";
import { formatAmount } from "../../../../core/primitives/money";
import { canTakeTable, heldTables, hostNotes, refundNotes, tablesLeft } from "../../../../core/primitives/vendor-show";
import {
  type OpenTablePayment,
  type PaymentPhase,
  VendorShowsSectionActions,
  VendorShowsSectionState,
} from "./vendor-shows-section-model";

const clock = (at: Date) => at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function VendorShowRow({
  show,
  mayTake,
  busyId,
  payment,
  phase,
  confirmed,
}: {
  show: VendorShow;
  mayTake: boolean;
  busyId: string | null;
  payment: OpenTablePayment | null;
  phase: PaymentPhase;
  confirmed: ReadonlySet<string>;
}) {
  const actions = useSubtree(VendorShowsSectionActions);
  const held = heldTables(show);
  const order = show.myOrder;
  const notes = refundNotes(show);
  const paying = payment?.eventId === show.id ? payment : null;
  return (
    <li className="panel space-y-3 p-3">
      <div className="space-y-1">
        <p className="font-medium">{show.title}</p>
        <p className="text-sm opacity-60">{showDates(show)}</p>
        <p className="truncate text-sm opacity-60">{venueLine(show.place)}</p>
        <p className="text-sm opacity-60">Hosted by {show.host.name ?? "another shop"}</p>
      </div>
      {show.cancelledAt ? (
        <p className="note note-warn">The host cancelled this show.</p>
      ) : (
        !show.onSale && <p className="text-sm opacity-60">This show is not taking tables now.</p>
      )}
      {held && <p className="note">Yours: {held}</p>}
      {hostNotes(show).map((note) => (
        <div key={note.id} className="note space-y-1">
          <p className="text-xs opacity-60">{note.heading}</p>
          <p className="whitespace-pre-line">{note.text}</p>
        </div>
      ))}
      {notes.map((note) => (
        <p key={note.id} className="note">
          {note.text}
        </p>
      ))}
      {paying && phase === "confirming" ? (
        <p className="note">
          Table {paying.order.tableLabel} ({paying.order.kindName}): payment received. Waiting for Stripe's word…
        </p>
      ) : order && confirmed.has(order.id) ? (
        <p className="note">
          Table {order.tableLabel} ({order.kindName}): payment received. The table is yours as soon as Stripe confirms
          it; read the shows again in a moment.
        </p>
      ) : (
        order && (
          <div className="note flex flex-wrap items-center gap-3">
            <p className="min-w-48 flex-1">
              Table {order.tableLabel} ({order.kindName}) is held for you until {clock(order.expiresAt)}. To pay:{" "}
              {formatAmount(order.amountCents, order.currency)}, fees included.
            </p>
            {mayTake && (
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  disabled={busyId !== null || payment !== null}
                  onClick={() => void actions.pay(show.id)}
                >
                  {busyId === order.id ? "Opening…" : "Pay"}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busyId !== null || payment !== null || phase !== "open"}
                  onClick={() => void actions.cancel(show.id)}
                >
                  {busyId === show.id ? "Giving it up…" : "Give it up"}
                </button>
              </div>
            )}
          </div>
        )
      )}
      {show.onSale && !show.takesPayments && (
        <p className="text-sm opacity-60">The host is not taking payments yet, so its tables are not on sale.</p>
      )}
      {!show.onSale ? null : show.kinds.length === 0 ? (
        <p className="text-sm opacity-60">The host has not put out any tables yet.</p>
      ) : (
        <ul className="space-y-2">
          {show.kinds.map((kind) => (
            <li key={kind.id} className="flex flex-wrap items-center gap-3 rounded-row border border-line px-3.5 py-3">
              <div className="min-w-48 flex-1 space-y-0.5">
                <p className="text-[13px] font-medium text-white">
                  {kind.name} · {formatAmount(kind.priceCents, kind.currency)}
                </p>
                {kind.description && <p className="text-xs text-muted">{kind.description}</p>}
                <p className="text-xs text-muted">{tablesLeft(kind)}</p>
              </div>
              {mayTake && !paying && canTakeTable(show, kind) && (
                <button
                  type="button"
                  className="btn-primary btn-sm shrink-0"
                  disabled={busyId !== null || payment !== null || phase !== "open"}
                  onClick={() => void actions.take(show.id, kind.id)}
                >
                  {busyId === kind.id ? "Holding it…" : "Take a table"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Reservations({
  reservations,
  mayTake,
  busyId,
  blocked,
  codeLabel,
}: {
  reservations: readonly TableReservation[];
  mayTake: boolean;
  busyId: string | null;
  blocked: boolean;
  codeLabel: string;
}) {
  const state = useSubtree(VendorShowsSectionState);
  const actions = useSubtree(VendorShowsSectionActions);
  const observer = useObserver();
  const code = observer.watch(state.code);
  if (!mayTake && reservations.length === 0) return null;
  return (
    <div className="panel space-y-3 p-3">
      {reservations.length > 0 && (
        <ul className="space-y-2">
          {reservations.map((r) => (
            <li key={r.id} className="note flex flex-wrap items-center gap-3">
              <p className="min-w-48 flex-1">
                Reserved for {r.email}: table {r.tableLabel} ({r.kindName}) at {r.event.title}.
              </p>
              {mayTake && r.code && (
                <button
                  type="button"
                  className="btn-primary btn-sm shrink-0"
                  disabled={busyId !== null || blocked}
                  onClick={() => void actions.redeem(r.code ?? "")}
                >
                  {busyId === `redeem:${r.code}` ? "Adding…" : "Add to this shop"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {mayTake && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void actions.redeem();
          }}
        >
          <label className="min-w-48 flex-1 space-y-1">
            <span className="text-sm opacity-60">{codeLabel}</span>
            <input
              className="field w-full"
              value={code}
              placeholder="K7QX-M2PD"
              autoComplete="off"
              onChange={(e) => actions.setCode(e.target.value)}
            />
          </label>
          <button type="submit" className="btn-ghost" disabled={busyId !== null || blocked}>
            {busyId === `redeem:${code.trim()}` ? "Redeeming…" : "Redeem"}
          </button>
        </form>
      )}
    </div>
  );
}

/** `listShows: false` keeps to the reservation code, for a vendor who arrives with one. */
export function VendorShowsSection({
  listShows = true,
  codeLabel = "Bought a table on HobbyShop? Enter its reservation code.",
}: {
  listShows?: boolean;
  codeLabel?: string;
}) {
  const state = useSubtree(VendorShowsSectionState);
  const actions = useSubtree(VendorShowsSectionActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const shows = observer.watch(state.shows);
  const mayTake = observer.watch(state.mayTake);
  const busyId = observer.watch(state.busyId);
  const action = observer.watch(state.action);
  const payment = observer.watch(state.payment);
  const phase = observer.watch(state.paymentPhase);
  const confirmed = observer.watch(state.confirmed);
  const reservations = observer.watch(state.reservations);
  const sheet = payment && phase !== "confirming" ? payment : null;
  const sheetShow = sheet ? shows.find((s) => s.id === sheet.eventId) : undefined;

  return (
    <section className="space-y-2">
      {listShows && (
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="eyebrow">Shows to sell at</h2>
            <p className="text-sm opacity-60">
              Other shops' shows. A table you take is held for you for five minutes while you pay for it here, then
              becomes your booth there: one of your shop's locations for the show's days.
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost"
            disabled={status.kind === "loading" || sheet !== null}
            onClick={() => void actions.reload()}
          >
            Read again
          </button>
        </div>
      )}
      {status.kind === "error" && <p className="note note-danger">{status.message}</p>}
      {action.error && !sheet && <p className="note note-danger">{action.error}</p>}
      {action.saved && <p className="note">{action.saved}</p>}
      <Reservations
        reservations={reservations}
        mayTake={mayTake}
        busyId={busyId}
        blocked={payment !== null}
        codeLabel={codeLabel}
      />
      {listShows && status.kind === "loading" && shows.length === 0 && <p className="opacity-60">Loading shows…</p>}
      {listShows && status.kind === "loaded" && shows.length === 0 && (
        <p className="text-sm opacity-60">No other shop has a show coming up yet.</p>
      )}
      {listShows && shows.length > 0 && (
        <ul className="space-y-2">
          {shows.map((show) => (
            <VendorShowRow
              key={show.id}
              show={show}
              mayTake={mayTake}
              busyId={busyId}
              payment={payment}
              phase={phase}
              confirmed={confirmed}
            />
          ))}
        </ul>
      )}
      {sheet && (
        <TableCheckoutSheet
          order={sheet.order}
          show={sheetShow ? `${sheetShow.title}, ${showDates(sheetShow)}` : null}
          stripe={sheet.stripe}
          clientSecret={sheet.clientSecret}
          error={action.error}
          busy={phase !== "open" || busyId !== null}
          cancelling={busyId === sheet.eventId}
          onCancel={() => void actions.cancel(sheet.eventId)}
          onStart={actions.paymentStarted}
          onFailed={actions.paymentFailed}
          onConfirmed={() => void actions.paymentConfirmed()}
        />
      )}
    </section>
  );
}
